// <accordion-list> — progressive enhancement for the Universal Accordion.
//
// The accordion is FULLY functional with no JS: rows are native <details>/<summary>,
// single-open is the native `name` attribute (set server-side), animation is CSS
// (interpolate-size + ::details-content). This element layers on the one thing CSS/HTML
// can't do alone: deep linking (open + scroll to a row from a #hash), with reduced-motion
// and history respected. Vanilla, self-registering, dependency-free — no @theme imports so
// it stays decoupled from Horizon internals across theme upgrades.
//
// Contract: any markup using snippets/accordion.liquid rows works here. Each row's
// deep-link target id lives on the .accordion__row element (or the <details> itself).

class AccordionList extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.addEventListener('hashchange', () => this.openFromHash(false), { signal });

    // Open-on-load for a matching anchor. Defer one frame so layout (sticky header
    // height var, fonts) has settled before we measure the scroll target.
    requestAnimationFrame(() => this.openFromHash(true));
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Open and scroll to the row matching the current location hash, if it lives in
   * this accordion. No-op when the hash targets another element on the page.
   * @param {boolean} initial - true on first load (instant scroll to avoid jank).
   */
  openFromHash(initial) {
    const raw = location.hash.replace(/^#/, '');
    if (!raw) return;

    let id;
    try {
      id = decodeURIComponent(raw);
    } catch {
      id = raw;
    }

    const escaped = window.CSS && CSS.escape ? CSS.escape(id) : id;
    const target = this.querySelector(`#${escaped}`);
    if (!target) return;

    const details = target.matches('details') ? target : target.querySelector('details');
    // Setting `open` on a named <details> natively closes its exclusive siblings.
    if (details && !details.open) details.open = true;

    target.scrollIntoView({
      behavior: initial || this.reducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }
}

if (!customElements.get('accordion-list')) {
  customElements.define('accordion-list', AccordionList);
}
